import type { SessionUser } from '../shared/types'
import { openPalette } from './palette'
import { internalLink } from './router'
import { signOut } from './session'
import { h } from './ui'

export type NavKey = 'new' | 'mine' | 'tokens' | 'passkeys' | null

export interface FrameOptions {
  user: SessionUser | null
  active?: NavKey
  /** Views with no per-view controls (sign-in, 404) skip the second row. */
  subbar?: boolean
}

export interface Frame {
  element: HTMLElement
  /** The second header row, where each view puts its own controls. */
  controls: HTMLElement
  stage: HTMLElement
  statusLeft: HTMLElement
  statusRight: HTMLElement
  insertBelowBar: (node: HTMLElement) => void
}

export function buildFrame(options: FrameOptions): Frame {
  const controls = h('div', { class: 'subbar' })
  const stage = h('div', { class: 'stage' })
  const statusLeft = h('span', { class: 'left' })
  const statusRight = h('span', { class: 'right' })

  const nav = h('nav', { class: 'nav' })
  if (options.user) {
    nav.append(
      navLink('New', '/', options.active === 'new'),
      navLink('My pastas', '/mine', options.active === 'mine'),
      navLink('Tokens', '/tokens', options.active === 'tokens', true),
      navLink('Passkeys', '/passkeys', options.active === 'passkeys', true),
      paletteHint(),
    )
  }

  const bar = h('header', { class: 'bar' }, [
    h('a', { class: 'brand', ...internalLink('/') }, [
      brandMark(),
      h('span', {}, ['pasta']),
      h('b', {}, ['bin']),
    ]),
    nav,
    userArea(options.user),
  ])

  const children: (HTMLElement | null)[] = [bar]
  if (options.subbar !== false) children.push(controls)
  children.push(stage, h('footer', { class: 'status' }, [statusLeft, statusRight]))

  const element = h('div', { class: 'frame' }, children)

  return {
    element,
    controls,
    stage,
    statusLeft,
    statusRight,
    insertBelowBar: (node) => element.insertBefore(node, stage),
  }
}

/**
 * A bowl of spaghetti, inline so it follows the active sauce: the bowl takes
 * --accent and the strands --syn-meta, the same pairing /favicon.svg uses.
 * Assigned as markup because h() speaks createElement, which cannot mint the
 * namespaced nodes an <svg> subtree needs. The string is a literal — no input
 * reaches it.
 */
function brandMark(): HTMLElement {
  const wrap = h('span', { class: 'mark', 'aria-hidden': 'true' })
  wrap.innerHTML =
    '<svg viewBox="0 0 20 20" focusable="false">' +
    '<g stroke="var(--syn-meta)" stroke-width="1.2" fill="none" stroke-linecap="round">' +
    '<path d="M6.25 6.4q1.25-1.15 2.5 0t2.5 0t2.5 0"/>' +
    '<path d="M5 8.25q1.25-1.15 2.5 0t2.5 0t2.5 0t2.5 0"/>' +
    '<path d="M4 10.1q1.5-1.2 3 0t3 0t3 0t3 0"/>' +
    '</g>' +
    '<path d="M2.8 11h14.4a7.2 7.2 0 0 1-14.4 0Z" fill="var(--accent)"/>' +
    '</svg>'
  return wrap
}

/**
 * The palette is a keyboard feature, and a keyboard feature nobody knows about
 * is no feature. This is how you find out it exists — and it's also the only
 * way in on a touch screen, where there is no Mod-K to press.
 */
function paletteHint(): HTMLElement {
  const mac = /Mac|iPhone|iPad/.test(navigator.userAgent)
  return h(
    'button',
    {
      class: 'palette-open',
      type: 'button',
      title: 'Search everything',
      onclick: () => void openPalette(),
    },
    [h('span', {}, ['Search']), h('kbd', {}, [mac ? '⌘K' : 'Ctrl K'])],
  )
}

function navLink(label: string, path: string, active: boolean, hideSmall = false): HTMLElement {
  return h(
    'a',
    {
      class: `navlink${active ? ' active' : ''}${hideSmall ? ' hide-sm' : ''}`,
      'aria-current': active ? 'page' : undefined,
      ...internalLink(path),
    },
    [label],
  )
}

function userArea(user: SessionUser | null): HTMLElement {
  // Signed out, the header stays quiet: signing in happens on the page itself,
  // because it needs a user gesture to invoke the authenticator.
  if (!user) return h('div', { class: 'user' })

  const initial = user.label.trim().charAt(0).toUpperCase() || '?'

  return h('div', { class: 'user' }, [
    h('span', { class: 'avatar fallback', 'aria-hidden': 'true' }, [initial]),
    h('span', { class: 'email hide-sm', title: user.label }, [user.label]),
    h(
      'button',
      { class: 'control', type: 'button', onclick: () => void signOut(), title: 'Sign out' },
      ['Sign out'],
    ),
  ])
}
